"use client";

import { instantMeiliSearch } from "@meilisearch/instant-meilisearch";

const HOST = process.env.NEXT_PUBLIC_MEILISEARCH_URL;
const API_KEY = process.env.NEXT_PUBLIC_MEILISEARCH_API_KEY;

const cache: { client: ReturnType<typeof instantMeiliSearch> | null } = {
  client: null,
};

/**
 * instant-meilisearch caches every search response in memory for the life of
 * the client, with no TTL. Combined with the singleton below, an empty
 * result (e.g. a creator searched before scrape/index finished) is replayed
 * for the rest of the SPA session even after Meilisearch has the document.
 *
 * Replaces `search` so every request clears the search response cache first,
 * ensuring each query fetches live data while leaving the facet-distribution
 * cache untouched.
 */
const disableSearchResponseCache = (
  client: ReturnType<typeof instantMeiliSearch>,
): ReturnType<typeof instantMeiliSearch> => {
  const { searchClient } = client;
  const originalSearch = searchClient.search.bind(searchClient) as (
    ...args: unknown[]
  ) => ReturnType<typeof searchClient.search>;

  searchClient.search = ((...args: unknown[]) => {
    searchClient.clearCache();
    return originalSearch(...args);
  }) as typeof searchClient.search;

  return client;
};

/**
 * Returns a stable singleton Meilisearch client.
 * Creating a new client on each call would cause InstantSearch to reinitialise
 * (resetting accumulated infinite-scroll hits) every time the parent component
 * re-renders.
 */
export const meilisearchClient = () => {
  if (!HOST || !API_KEY) {
    throw new Error("MeiliSearch host or API key not found");
  }
  if (!cache.client) {
    cache.client = disableSearchResponseCache(instantMeiliSearch(HOST, API_KEY));
  }
  return cache.client;
};
